import type { AppProps } from 'next/app';
import '@/assets/css/main.css';
import '@/assets/css/custom-plugins.css';
import '@/assets/css/rich-text-editor.css';
import 'react-toastify/dist/ReactToastify.css';
import { ModalProvider } from '@/components/ui/modal/modal.context';
import LazyManagedModal from '@/components/ui/modal/lazy-managed-modal';
import LazyManagedDrawer from '@/components/ui/drawer/lazy-managed-drawer';
import DefaultSeo from '@/components/seo/default-seo';
import { SearchProvider } from '@/components/ui/search/search.context';
import PrivateRoute from '@/lib/private-route';
import { CartProvider } from '@/store/quick-cart/cart.context';
import { CartSyncBootstrap } from '@/components/cart/cart-sync-bootstrap';
import { NextPageWithLayout } from '@/types';
import QueryProvider from '@/framework/client/query-provider';
import { getDirection } from '@/lib/constants';
import { useAppLocale } from '@/lib/hooks/use-app-locale';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
const ToastContainer = dynamic(
  () => import('react-toastify').then((module) => module.ToastContainer),
  { ssr: false },
);
const RouletteWidget = dynamic(
  () => import('@/components/roulette/roulette-widget'),
  { ssr: false },
);
const PromotionalSticker = dynamic(
  () => import('@/components/promotions/promotional-sticker'),
  { ssr: false },
);
const CartBackgroundPriceUpdater = dynamic(
  () => import('@/components/cart/cart-background-price-updater'),
  { ssr: false },
);
const CartPbmComboGuard = dynamic(
  () => import('@/components/cart/cart-pbm-combo-verifier'),
  { ssr: false },
);
const SocialLogin = dynamic(() => import('@/components/auth/social-login'), {
  ssr: false,
});
const NativeDeeplinkHandler = dynamic(
  () => import('@/components/common/native-deeplink-handler'),
  { ssr: false },
);
const NetcoreHandler = dynamic(
  () => import('@/components/common/netcore-handler'),
  { ssr: false },
);
const OldWebViewDetector = dynamic(
  () => import('@/components/common/old-webview-detector'),
  { ssr: false },
);
import ApiHealthGate from '@/components/infra-maintenance/api-health-gate';
import Maintenance from '@/components/maintenance/layout';
import { NotificationProvider } from '@/context/notify-content';
import ubuntu from '@/lib/fonts/ubuntu';
import { gtmPageView } from '@/lib/gtm';
import { useEffect } from 'react';
import { datadogInit } from '@/lib/datadog';
import { grafanaFaroInit } from '@/lib/grafana-faro';
import { isGrafanaFaroEnabled } from '@/lib/grafana-faro-enabled';
import { retireLegacyPwa } from '@/lib/register-service-worker';
import { captureCheckoutAttribution } from '@/lib/checkout-attribution';

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export function OldAppDetectorTest() {


  return null;
}

function CustomApp({ Component, pageProps }: AppPropsWithLayout) {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  const authenticationRequired = Component.authenticationRequired ?? false;
  const locale = useAppLocale();
  const dir = getDirection(locale);
  const router = useRouter();

  useEffect(() => {
    datadogInit();
    if (isGrafanaFaroEnabled) {
      void grafanaFaroInit();
    }
    retireLegacyPwa();
    captureCheckoutAttribution(router.asPath);
    const handleRouteChange = (url: string) => {
      gtmPageView(url);
      captureCheckoutAttribution(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router]);

  return (
    <div dir={dir} className={ubuntu.variable}>
      <QueryProvider pageProps={pageProps}>
        <ApiHealthGate>
          <SearchProvider>
            <ModalProvider>
              <CartProvider>
                <>
                  <CartSyncBootstrap />
                  <CartBackgroundPriceUpdater />
                  <CartPbmComboGuard />
                  <DefaultSeo />
                  <Maintenance>
                    <NotificationProvider>
                      {authenticationRequired ? (
                        <PrivateRoute>
                          {getLayout(<Component {...pageProps} />)}
                        </PrivateRoute>
                      ) : (
                        getLayout(<Component {...pageProps} />)
                      )}
                    </NotificationProvider>
                  </Maintenance>
                  <LazyManagedModal />
                  <LazyManagedDrawer />
                  <RouletteWidget />
                  <PromotionalSticker />
                  <ToastContainer autoClose={5000} theme="colored" />
                  <SocialLogin />
                  <NativeDeeplinkHandler />
                  <NetcoreHandler />
                  <OldWebViewDetector />
                </>
              </CartProvider>
            </ModalProvider>
          </SearchProvider>
        </ApiHealthGate>
      </QueryProvider>
    </div>
  );
}

export default CustomApp;
